import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/components/ThemeProvider';
import ChatbotWidget from '@/components/ChatbotWidget';
import NotificationBell from '@/components/NotificationBell';
import UserBadge from '@/components/UserBadge';
import { TrialBanner } from '@/components/ProFeatureGate';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  LayoutDashboard, Upload, Receipt, DollarSign, TrendingUp,
  PieChart, Users, Sun, Moon, Monitor, LogOut, Menu, Wallet, ChevronRight, LineChart, Settings as SettingsIcon, KeyRound, Info, Activity, MessageCircleQuestion, Bell, Heart, Twitter, Instagram, ChevronsLeft, ChevronsRight, MessageSquare
} from 'lucide-react';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/income', icon: DollarSign, label: 'Income' },
  { to: '/investments', icon: LineChart, label: 'Investments' },
  { to: '/networth', icon: Wallet, label: 'Net Worth' },
  { to: '/upload', icon: Upload, label: 'Uploads' },
  { to: '/transactions', icon: Receipt, label: 'Transactions' },
  { to: '/analytics', icon: PieChart, label: 'Analytics' },
  { to: '/support', icon: Heart, label: 'Support' },
  { to: '/feedback', icon: MessageSquare, label: 'Feedback' },
  { to: '/settings', icon: SettingsIcon, label: 'Settings' },
  { to: '/about', icon: Info, label: 'About' },
];

const adminItems = [
  { to: '/admin', icon: Users, label: 'Admin' },
  { to: '/admin/notifications', icon: Bell, label: 'Notifications' },
  { to: '/admin/health', icon: Activity, label: 'Health' },
  { to: '/admin/subscriptions', icon: Heart, label: 'Subscriptions' },
];

function NavItem({ to, icon: Icon, label, onClick, isCollapsed }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
      title={isCollapsed ? label : ''}
      className={({ isActive }) =>
        `flex items-center ${isCollapsed ? 'justify-center' : 'gap-2.5'} px-3 py-2 text-sm font-medium rounded-md transition-all duration-150
        ${isActive
          ? 'bg-primary/10 text-primary border-l-2 border-primary'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!isCollapsed && <span>{label}</span>}
    </NavLink>
  );
}

function SidebarContent({ onClose, onOpenChat, isCollapsed, onToggleCollapse }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const items = isAdmin ? [...navItems, ...adminItems] : navItems;
  // Backend-sourced version (falls back to literal if endpoint fails)
  const [versionLabel, setVersionLabel] = useState('v1.5.0');
  useEffect(() => {
    api.get('/api/system/version')
      .then(({ data }) => { if (data?.version) setVersionLabel(data.version); })
      .catch(() => { /* keep fallback */ });
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className={`px-3 py-3 border-b border-border flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        <div 
          className={`flex items-center gap-2 cursor-pointer ${isCollapsed ? 'justify-center' : ''}`}
          onClick={() => { navigate('/dashboard'); if (onClose) onClose(); }} 
          data-testid="moneymap-logo"
        >
          <div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center shrink-0">
            <TrendingUp className="h-3.5 w-3.5 text-primary-foreground" />
          </div>
          {!isCollapsed && <h1 className="text-base font-semibold tracking-tight font-['Outfit']">TheMoneyMap</h1>}
        </div>
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="p-1 hover:bg-accent rounded transition-colors"
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid={isCollapsed ? "expand-sidebar-btn" : "collapse-sidebar-btn"}
          >
            {isCollapsed ? (
              <ChevronsRight className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronsLeft className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        )}
      </div>
      <nav className="flex-1 px-2.5 py-3 space-y-0.5 overflow-y-auto">
        {!isCollapsed && <p className="px-3 mb-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Overview</p>}
        {items.map((item) => (
          <NavItem key={item.to} {...item} onClick={onClose} isCollapsed={isCollapsed} />
        ))}
        
        {/* Help/Chat button */}
        <button
          onClick={() => {
            onOpenChat();
            if (onClose) onClose();
          }}
          title={isCollapsed ? "Help" : ''}
          data-testid="nav-help"
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2.5'} px-3 py-2 text-sm font-medium rounded-md transition-all duration-150 text-muted-foreground hover:bg-accent hover:text-accent-foreground mt-1`}
        >
          <MessageCircleQuestion className="h-4 w-4 shrink-0" />
          {!isCollapsed && <span>Help</span>}
        </button>
      </nav>
      {!isCollapsed && (
        <div className="px-3 py-2 border-t border-border text-[10px] text-muted-foreground space-y-0.5">
          <div data-testid="sidebar-version">TheMoneyMap {versionLabel}</div>
          <a href="mailto:info@themoneymap.online" className="block text-[10px] hover:text-foreground transition-colors" data-testid="support-email-link">info@themoneymap.online</a>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showPwdDialog, setShowPwdDialog] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const chatbotRef = useRef(null);
  const [versionLabel, setVersionLabel] = useState('v1.5.0');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Fetch version from backend
  useEffect(() => {
    api.get('/api/system/version')
      .then(({ data }) => { if (data?.version) setVersionLabel(data.version); })
      .catch(() => { /* keep fallback */ });
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleOpenChat = () => {
    chatbotRef.current?.open();
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('sidebarCollapsed', newValue);
      return newValue;
    });
  };

  const themeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const ThemeIcon = themeIcon;

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex flex-col border-r border-border bg-card shrink-0 transition-all duration-300 ${sidebarCollapsed ? 'md:w-16' : 'md:w-52 lg:w-56'}`}>
        <SidebarContent onOpenChat={handleOpenChat} isCollapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="h-14 border-b border-border bg-card flex items-center justify-between px-4 shrink-0">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-56 p-0">
                <SidebarContent onClose={() => setOpen(false)} onOpenChat={handleOpenChat} />
              </SheetContent>
            </Sheet>
            <div className="hidden md:flex items-center gap-1 text-sm text-muted-foreground">
              <ChevronRight className="h-3 w-3" />
              <span className="capitalize">{window.location.pathname.slice(1) || 'dashboard'}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <NotificationBell />
            
            {/* User Badge */}
            {user?.badge && (
              <UserBadge badge={user.badge} size="sm" />
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="theme-toggle-btn">
                  <ThemeIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setTheme('light')} data-testid="theme-light">
                  <Sun className="h-4 w-4 mr-2" /> Light
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')} data-testid="theme-dark">
                  <Moon className="h-4 w-4 mr-2" /> Dark
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')} data-testid="theme-system">
                  <Monitor className="h-4 w-4 mr-2" /> System
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-btn">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowPwdDialog(true)} data-testid="change-password-menu">
                  <KeyRound className="h-4 w-4 mr-2" /> Change Password
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="mailto:info@themoneymap.online" className="flex items-center cursor-pointer">
                    <MessageCircleQuestion className="h-4 w-4 mr-2" /> Contact Support
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Change Password modal — moved here from Settings (v1.3.1 §8) */}
        <Dialog open={showPwdDialog} onOpenChange={setShowPwdDialog}>
          <DialogContent data-testid="change-password-dialog">
            <DialogHeader><DialogTitle className="font-['Outfit']">Change Password</DialogTitle></DialogHeader>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (newPwd.length < 8) { toast.error('Password must be ≥ 8 chars'); return; }
              setPwdSaving(true);
              try {
                await api.patch('/api/auth/me', { password: newPwd });
                toast.success('Password updated');
                setNewPwd(''); setShowPwdDialog(false);
              } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
              finally { setPwdSaving(false); }
            }} className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">New Password</Label>
                <Input type="password" data-testid="new-password-input" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Minimum 8 characters" required />
              </div>
              <Button type="submit" className="w-full" disabled={pwdSaving} data-testid="change-password-submit">{pwdSaving ? 'Updating...' : 'Update Password'}</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Page content with footer inside scrollable area */}
        <main className="flex-1 overflow-y-auto">
          {/* Trial Banner */}
          <TrialBanner />
          
          <div className="min-h-full flex flex-col">
            <div className="flex-1 p-4 md:p-6">
              {children}
            </div>
            
            {/* Footer - only visible when scrolled to bottom */}
            <footer className="border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 mt-auto">
              <div className="px-4 md:px-6 py-4">
                <div className="flex flex-col items-center justify-center gap-3 text-xs text-muted-foreground">
                  {/* Top Row: App Name & Copyright */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="h-5 w-5 rounded bg-primary/10 flex items-center justify-center">
                        <TrendingUp className="h-3 w-3 text-primary" />
                      </div>
                      <span className="font-medium text-sm">TheMoneyMap {versionLabel}</span>
                    </div>
                    <span className="text-muted-foreground/50">•</span>
                    <span>© {new Date().getFullYear()} TheMoneyMap. All rights reserved.</span>
                  </div>
                  
                  {/* Bottom Row: Social Media - Centered */}
                  <div className="flex items-center gap-4">
                    <a 
                      href="https://twitter.com/themoneymapca" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 hover:text-foreground transition-colors group"
                      aria-label="Follow us on X (Twitter)"
                    >
                      <div className="p-1 group-hover:bg-muted rounded">
                        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </div>
                      <span className="font-medium">@themoneymapca</span>
                    </a>
                    <span className="text-muted-foreground/50">•</span>
                    <a 
                      href="https://instagram.com/themoneymap.online" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 hover:text-foreground transition-colors group"
                      aria-label="Follow us on Instagram"
                    >
                      <div className="p-1 group-hover:bg-muted rounded">
                        <Instagram className="h-3.5 w-3.5" />
                      </div>
                      <span className="font-medium">@themoneymap.online</span>
                    </a>
                  </div>

                  {/* Security Badge Row */}
                  <div className="flex items-center gap-2 text-muted-foreground/80 pt-1">
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="font-medium inline-flex items-center gap-1.5">
                      Secured by
                      <svg className="h-3 w-3" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Google Cloud
                    </span>
                    <span className="text-muted-foreground/50">•</span>
                    <span className="font-medium">256-bit Encryption</span>
                  </div>
                </div>
              </div>
            </footer>
          </div>
        </main>

        {/* Chatbot Widget */}
        <ChatbotWidget ref={chatbotRef} />
      </div>
    </div>
  );
}
